export const CITATION_HIGHLIGHT_CLASS = "citation-excerpt-highlight";

const MIN_MATCH_LENGTH = 24;

interface NormalizedText {
  text: string;
  map: number[];
}

/** Collapse whitespace and lowercase, keeping a map back to original indexes. */
export function normalizeForMatch(input: string): NormalizedText {
  const chars: string[] = [];
  const map: number[] = [];
  let lastWasSpace = true;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index] ?? "";
    if (/\s/.test(char)) {
      if (!lastWasSpace && chars.length > 0) {
        chars.push(" ");
        map.push(index);
        lastWasSpace = true;
      }
      continue;
    }

    chars.push(char.toLowerCase());
    map.push(index);
    lastWasSpace = false;
  }

  if (chars[chars.length - 1] === " ") {
    chars.pop();
    map.pop();
  }

  return { text: chars.join(""), map };
}

function findWindow(
  haystack: string,
  needle: string,
  length: number,
): { start: number; length: number } | null {
  for (let index = 0; index + length <= needle.length; index += 1) {
    const found = haystack.indexOf(needle.slice(index, index + length));
    if (found >= 0) {
      return { start: found, length };
    }
  }
  return null;
}

/** Indexes into the original page text that should be highlighted. */
export function findExcerptRange(
  pageText: string,
  excerpt: string,
): { start: number; end: number } | null {
  const page = normalizeForMatch(pageText);
  const quote = normalizeForMatch(excerpt);
  if (!page.text || quote.text.length < 8) {
    return null;
  }

  let matchStart = page.text.indexOf(quote.text);
  let matchLength = quote.text.length;

  if (matchStart < 0) {
    let best: { start: number; length: number } | null = null;
    let low = MIN_MATCH_LENGTH;
    let high = quote.text.length - 1;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const found = findWindow(page.text, quote.text, mid);
      if (found) {
        best = found;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    if (!best) {
      return null;
    }
    matchStart = best.start;
    matchLength = best.length;
  }

  const start = page.map[matchStart];
  const end = (page.map[matchStart + matchLength - 1] ?? start) + 1;
  if (start == null || end <= start) {
    return null;
  }

  return { start, end };
}

export function highlightExcerptInPage(pageRoot: HTMLElement, excerpt: string): HTMLElement | null {
  const layer = pageRoot.querySelector(".react-pdf__Page__textContent");
  if (!layer) {
    return null;
  }

  const spans = [...layer.querySelectorAll("span")];
  for (const span of spans) {
    span.classList.remove(CITATION_HIGHLIGHT_CLASS);
  }

  const pieces: { span: HTMLElement; start: number; end: number }[] = [];
  let raw = "";
  for (const span of spans) {
    const text = span.textContent ?? "";
    if (raw && text && !/\s$/.test(raw) && !/^\s/.test(text)) {
      raw += " ";
    }
    const start = raw.length;
    raw += text;
    pieces.push({ span, start, end: raw.length });
  }

  const range = findExcerptRange(raw, excerpt);
  if (!range) {
    return null;
  }

  let first: HTMLElement | null = null;
  for (const piece of pieces) {
    if (piece.end <= range.start || piece.start >= range.end || piece.start === piece.end) {
      continue;
    }
    piece.span.classList.add(CITATION_HIGHLIGHT_CLASS);
    if (!first) {
      first = piece.span;
    }
  }

  return first;
}
